import { useMemo, useState } from 'react';
import { ErrorBanner, PageHeader, PageLoading } from '@/components/ui';
import { PageContainer } from '@/components/layout';
import {
  BimGraphCanvas,
  BimGraphLegend,
  BimGraphToolbar,
  BimRelationshipList,
  BimSelectionPanels,
} from '@/components/bim';
import { useApi } from '@/hooks/useApi';
import { schemaRelationshipApi } from '@/services/schema-relationship-api';
import { layoutGraph } from '@/lib/bim-graph-layout';
import { routeEdges } from '@/lib/bim-edge-routing';
import { computeHighlight } from '@/lib/bim-selection';
import type { IBimRelationshipEdge } from '@skystone/shared';

function matchesSearch(edge: IBimRelationshipEdge, query: string): boolean {
  if (query.length === 0) {
    return true;
  }

  const haystack = `${edge.fromTable} ${edge.fromColumn} ${edge.toTable} ${edge.toColumn}`.toLowerCase();
  return haystack.includes(query);
}

function isOneToMany(edge: IBimRelationshipEdge): boolean {
  return edge.cardinality === 'one_to_many' || edge.cardinality === 'many_to_one';
}

export function BimRelationshipsPage() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [onlyOneToMany, setOnlyOneToMany] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const { data, loading, error } = useApi(
    () => schemaRelationshipApi.getBimGraph(includeInactive),
    [includeInactive],
  );

  const nodes = useMemo(() => data?.nodes ?? [], [data?.nodes]);
  const allEdges = useMemo(() => data?.edges ?? [], [data?.edges]);

  const visibleEdges = useMemo(() => {
    const query = search.trim().toLowerCase();
    return allEdges.filter((edge) => {
      if (onlyOneToMany && !isOneToMany(edge)) {
        return false;
      }
      return matchesSearch(edge, query);
    });
  }, [allEdges, onlyOneToMany, search]);

  const layout = useMemo(() => layoutGraph(nodes, allEdges), [nodes, allEdges]);
  const routedEdges = useMemo(() => routeEdges(visibleEdges, layout.cards), [visibleEdges, layout]);
  const highlight = useMemo(
    () => computeHighlight(nodes, visibleEdges, selectedNodeId, selectedEdgeId),
    [nodes, visibleEdges, selectedNodeId, selectedEdgeId],
  );

  // Resolve the selection against the currently visible data so a filter or
  // reload that removes the selected item cannot leave a stale "selected" state.
  const selectedEdge = visibleEdges.find((edge) => edge.id === selectedEdgeId) ?? null;
  const selectedNode = nodes.find((node) => node.id === selectedNodeId) ?? null;
  const hasSelection = selectedNode !== null || selectedEdge !== null;

  const handleSelectNode = (nodeId: string): void => {
    setSelectedNodeId(nodeId);
    setSelectedEdgeId(null);
  };

  const handleSelectEdge = (edgeId: string): void => {
    setSelectedEdgeId(edgeId);
    setSelectedNodeId(null);
  };

  const handleClearSelection = (): void => {
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
  };

  if (loading && !data) {
    return <PageLoading />;
  }

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader
        title="BIM Relationships"
        subtitle="ERD view of key table relationships (LocalDateTable_* excluded)."
      />

      <ErrorBanner message={error} />

      <BimGraphToolbar
        graph={data ?? null}
        tableCount={nodes.length}
        edgeCount={visibleEdges.length}
        search={search}
        includeInactive={includeInactive}
        onlyOneToMany={onlyOneToMany}
        showLabels={showLabels}
        hasSelection={hasSelection}
        onSearchChange={setSearch}
        onIncludeInactiveChange={setIncludeInactive}
        onOnlyOneToManyChange={setOnlyOneToMany}
        onShowLabelsChange={setShowLabels}
        onClearSelection={handleClearSelection}
      />

      <div className="rounded-md border border-border bg-card p-3">
        <BimGraphLegend />
        <div className="mt-3 h-[68vh] min-h-[480px] w-full overflow-hidden rounded border border-border bg-background/70">
          <BimGraphCanvas
            layout={layout}
            nodes={nodes}
            edges={visibleEdges}
            routedEdges={routedEdges}
            selectedNodeId={selectedNodeId}
            selectedEdgeId={selectedEdgeId}
            dimmedNodeIds={highlight.dimmedNodeIds}
            dimmedEdgeIds={highlight.dimmedEdgeIds}
            highlightedColumnsByNode={highlight.highlightedColumnsByNode}
            showLabels={showLabels}
            onSelectNode={handleSelectNode}
            onSelectEdge={handleSelectEdge}
          />
        </div>
      </div>

      <BimRelationshipList
        edges={visibleEdges}
        selectedEdgeId={selectedEdgeId}
        onSelectEdge={handleSelectEdge}
      />

      <BimSelectionPanels selectedEdge={selectedEdge} selectedNode={selectedNode} />
    </PageContainer>
  );
}
